import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, Eye, Edit, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { branchService } from '@/services/branchService'
import { BranchEntity } from '@/api/types'

/**
 * Branches List Page
 * Displays a table of branches with CRUD operations
 */
export const BranchesList: React.FC = () => {
  const navigate = useNavigate()
  
  const [branches, setBranches] = useState<BranchEntity[]>([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  // Fetch branches
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        setLoading(true)
        const data = await branchService.getAllBranches()
        setBranches(data || [])
      } catch (error: any) {
        console.error('Error fetching branches:', error)
        toast.error(error.response?.data?.message || 'Failed to fetch branches')
      } finally {
        setLoading(false)
      }
    }

    fetchBranches()
  }, [])

  // Client-side search filtering
  const filteredBranches = useMemo(() => {
    if (!searchQuery.trim()) {
      return branches
    }

    const query = searchQuery.toLowerCase()
    return branches.filter((branch) => {
      const nameEn = (branch.nameEn || '').toLowerCase()
      const nameAr = (branch.nameAr || '').toLowerCase()
      const email = (branch.email || '').toLowerCase()
      const phone = (branch.phone || '').toLowerCase()
      const addressEn = (branch.addressEn || '').toLowerCase()
      const addressAr = (branch.addressAr || '').toLowerCase()

      return (
        nameEn.includes(query) ||
        nameAr.includes(query) ||
        email.includes(query) ||
        phone.includes(query) ||
        addressEn.includes(query) ||
        addressAr.includes(query)
      )
    })
  }, [branches, searchQuery])

  const handleAddBranch = () => {
    navigate('/branches/new')
  }

  const handleViewBranch = (id: number) => {
    navigate(`/branches/${id}/view`)
  }

  const handleEditBranch = (id: number) => {
    navigate(`/branches/${id}/edit`)
  }

  const handleDeleteBranch = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this branch?')) {
      return
    }

    try {
      setLoading(true)
      const response = await branchService.deleteBranch(id)
      if (response.success) {
        toast.success('Branch deleted successfully')
        // Refresh the list
        const data = await branchService.getAllBranches()
        setBranches(data || [])
      } else {
        toast.error(response.message || 'Failed to delete branch')
      }
    } catch (error: any) {
      console.error('Error deleting branch:', error)
      toast.error(error.response?.data?.message || 'Failed to delete branch')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      })
    } catch {
      return dateString
    }
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Branch Info"
        description="Branch Details"
        action={{
          label: 'Add New Branch',
          onClick: handleAddBranch,
          icon: Plus,
        }}
      />

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search branches by name, email, phone, or address..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Branches Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">Loading branches...</p>
            </div>
          ) : filteredBranches.length === 0 ? (
            <div className="p-12">
              <EmptyState
                title={searchQuery ? "No branches found" : "No branches found"}
                description={
                  searchQuery
                    ? "Try adjusting your search query"
                    : "Get started by adding your first branch to the system."
                }
                action={
                  !searchQuery
                    ? {
                        label: 'Add New Branch',
                        onClick: handleAddBranch,
                      }
                    : undefined
                }
              />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                    <TableHead className="h-12 px-3 bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Name
                    </TableHead>
                    <TableHead className="h-12 px-3 bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Address
                    </TableHead>
                    <TableHead className="h-12 px-3 bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Email
                    </TableHead>
                    <TableHead className="h-12 px-3 bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Contact No
                    </TableHead>
                    <TableHead className="h-12 px-3 bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Created Date
                    </TableHead>
                    <TableHead className="h-12 px-3 text-right bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Action
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBranches.map((branch) => (
                    <TableRow 
                      key={branch.id} 
                      className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors"
                    >
                      <TableCell className="px-3 py-2 font-medium text-sm">
                        {branch.nameEn || branch.nameAr || 'N/A'}
                      </TableCell>
                      <TableCell className="px-3 py-2 text-sm">
                        {branch.addressEn || branch.addressAr || 'N/A'}
                      </TableCell>
                      <TableCell className="px-3 py-2 text-sm">{branch.email || 'N/A'}</TableCell>
                      <TableCell className="px-3 py-2 text-sm">{branch.phone || 'N/A'}</TableCell>
                      <TableCell className="px-3 py-2 text-sm">
                        {formatDate(branch.createdDate)}
                      </TableCell>
                      <TableCell className="px-3 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewBranch(branch.id!)}
                            className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditBranch(branch.id!)}
                            className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteBranch(branch.id!)}
                            className="h-7 w-7 p-0 hover:bg-destructive/10 dark:hover:bg-destructive/20 text-destructive"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination Info */}
              <div className="border-t px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Showing {filteredBranches.length} of {branches.length} entries
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

